export interface WorkerFinancialInput {
	revenue: unknown;
	salary: unknown;
	expenses: unknown;
}

export interface WorkerFinancials {
	revenue: number;
	salary: number;
	expenses: number;
	totalExpenses: number;
	profitLoss: number;
}

const toFiniteNumber = (value: unknown): number => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : 0;
};

export const calculateWorkerFinancials = (input: WorkerFinancialInput): WorkerFinancials => {
	const revenue = toFiniteNumber(input.revenue);
	const salary = toFiniteNumber(input.salary);
	const expenses = toFiniteNumber(input.expenses);
	const totalExpenses = salary + expenses;

	return {
		revenue,
		salary,
		expenses,
		totalExpenses,
		profitLoss: revenue - totalExpenses,
	};
};

export const summarizeWorkerFinancials = (rows: WorkerFinancialInput[]): WorkerFinancials =>
	rows.reduce<WorkerFinancials>(
		(summary, row) => {
			const financials = calculateWorkerFinancials(row);
			return {
				revenue: summary.revenue + financials.revenue,
				salary: summary.salary + financials.salary,
				expenses: summary.expenses + financials.expenses,
				totalExpenses: summary.totalExpenses + financials.totalExpenses,
				profitLoss: summary.profitLoss + financials.profitLoss,
			};
		},
		{ revenue: 0, salary: 0, expenses: 0, totalExpenses: 0, profitLoss: 0 }
	);

export const formatCreditedQuantity = (value: unknown): string => {
	if (typeof value !== 'string' || value.trim() === '') return '—';

	const formatted = value
		.split('|')
		.map((entry) => {
			const separator = entry.lastIndexOf(':');
			if (separator <= 0) return null;
			const unit = entry.slice(0, separator).trim();
			const quantity = Number(entry.slice(separator + 1));
			if (!unit || !Number.isFinite(quantity)) return null;
			return `${quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit}`;
		})
		.filter((entry): entry is string => !!entry);

	return formatted.length > 0 ? formatted.join(' · ') : '—';
};

export interface WorkerWorkBreakdownItem {
	typeName: string;
	projectName: string;
	workDoneId: number;
	creditedQuantity: string;
	collaboratorNames: string;
}

export const parseWorkerWorkBreakdown = (value: unknown): WorkerWorkBreakdownItem[] => {
	if (typeof value !== 'string' || value.trim() === '') return [];

	return value
		.split('||')
		.map((entry) => {
			const [typeName, projectName, workDoneIdText, quantityText, collaboratorNames = ''] = entry.split('~');
			const workDoneId = Number(workDoneIdText);
			if (!typeName?.trim() || !projectName?.trim() || !Number.isFinite(workDoneId) || !quantityText) return null;
			return {
				typeName: typeName.trim(),
				projectName: projectName.trim(),
				workDoneId,
				creditedQuantity: formatCreditedQuantity(quantityText),
				collaboratorNames: collaboratorNames.trim(),
			};
		})
		.filter((item): item is WorkerWorkBreakdownItem => !!item);
};
